import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, Plus, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ProjectDto, projectsApi } from '../../services/projects';
import { SubProjectMiniCard } from './SubProjectMiniCard';
import './SubProjectList.css';

interface SubProjectListProps {
  parentProjectId: string;
  subProjectCount: number;
  completedSubProjectCount: number;
  initiallyExpanded?: boolean;
  onSubProjectClick?: (project: ProjectDto) => void;
  onAddSubProject?: () => void;
  isPieceList?: boolean; // When true, use "Pieces" terminology instead of "Sub-projects"
}

export const SubProjectList: React.FC<SubProjectListProps> = ({
  parentProjectId,
  subProjectCount,
  completedSubProjectCount,
  initiallyExpanded = false,
  onSubProjectClick,
  onAddSubProject,
  isPieceList = false,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(initiallyExpanded);
  const [subProjects, setSubProjects] = useState<ProjectDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadSubProjects = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const projects = await projectsApi.getSubProjects(parentProjectId);
      setSubProjects(projects);
      setHasLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load sub-projects');
    } finally {
      setIsLoading(false);
    }
  }, [parentProjectId]);

  // Load sub-projects when expanded for the first time
  useEffect(() => {
    if (isExpanded && !hasLoaded && subProjectCount > 0) {
      loadSubProjects();
    }
  }, [isExpanded, hasLoaded, subProjectCount, loadSubProjects]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const progressPercentage = subProjectCount > 0
    ? Math.round((completedSubProjectCount / subProjectCount) * 100)
    : 0;

  // Translations for piece terminology (SubProjectList is now only used for pieces)
  const addLabel = t('works.pieces.addPiece');
  const countLabel = t('works.pieces.count', { completed: completedSubProjectCount, total: subProjectCount });

  if (subProjectCount === 0) {
    return (
      <div className={`sub-project-list sub-project-list--empty ${isPieceList ? 'sub-project-list--pieces' : ''}`}>
        <button
          className="sub-project-list__add-btn"
          onClick={onAddSubProject}
          aria-label={addLabel}
        >
          <Plus size={14} />
          <span>{addLabel}</span>
        </button>
      </div>
    );
  }

  return (
    <div className={`sub-project-list ${isPieceList ? 'sub-project-list--pieces' : ''}`}>
      <button
        className="sub-project-list__header"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        aria-controls={`sub-projects-${parentProjectId}`}
      >
        <div className="sub-project-list__summary">
          <span className="sub-project-list__count">{countLabel}</span>
          <div className="sub-project-list__progress-bar">
            <div
              className="sub-project-list__progress-fill"
              style={{ width: `${progressPercentage}%` }}
              role="progressbar"
              aria-valuenow={progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        </div>
        <div className="sub-project-list__toggle">
          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {isExpanded && (
        <div
          id={`sub-projects-${parentProjectId}`}
          className="sub-project-list__content"
        >
          {isLoading && (
            <div className="sub-project-list__loading">
              <Loader2 size={16} className="sub-project-list__spinner" />
              <span>{t('works.pieces.loading')}</span>
            </div>
          )}

          {error && (
            <div className="sub-project-list__error">
              <span>{error}</span>
              <button onClick={loadSubProjects}>{t('common.tryAgain')}</button>
            </div>
          )}

          {!isLoading && !error && (
            <>
              <div className="sub-project-list__items">
                {subProjects.map((project) => (
                  <SubProjectMiniCard
                    key={project.id}
                    project={project}
                    onClick={onSubProjectClick}
                    isPiece={isPieceList}
                  />
                ))}
              </div>
              <button
                className="sub-project-list__add-inline"
                onClick={onAddSubProject}
              >
                <Plus size={14} />
                <span>{addLabel}</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SubProjectList;
